// App shell: topbar row + (sidebar + main) row. Both sidebar and main scroll
// independently. Auth still gates the whole shell.
//
// On mobile (<768px) the sidebar becomes a slide-in drawer over the main
// area; the topbar toggle flips between collapsed/expanded on desktop and
// open/closed on mobile.
//
// Shell height is `100dvh`, NOT `h-screen` (`100vh`). On a mobile browser
// `100vh` is TALLER than the visible area, because it excludes the collapsing
// address bar. Since this container is `overflow-hidden`, that surplus is
// clipped and unreachable: `<main>` scrolls internally, but its bottom edge sits
// below the fold, so the last strip of content can never be scrolled into view.
// Steven hit exactly that on 2026-08-04, reaching the end of a grid on his phone
// with the totals footer sliced in half and no way to scroll further.
// `dvh` tracks the visible viewport, so the shell's bottom edge and the screen's
// bottom edge are the same line. Already used elsewhere in this app
// (pages/stores/HqPhotos.tsx) and supported by every browser this app runs in.

import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { AuthGate } from './AuthGate';
import { CockpitHelpCenter } from '@/components/CockpitHelpCenter';
import { ChatBubbleMount } from './ChatBubbleMount';
import { useCollapsedSidebar } from '@/lib/useCollapsedSidebar';
import { useIsMobile, useServiceDeskUnread, type HelpCenterView } from '@ampersandbrands/ui';
import { useSession } from '@/lib/useSession';
import { isStoreOnlyUser } from '@/lib/useStores';
import { cockpitServiceDeskClient } from '@/lib/serviceDeskClient';
import { useDocumentTitle } from '@/lib/useDocumentTitle';
import { OPEN_HELP_EVENT, type OpenHelpDetail } from '@/lib/helpLauncher';

export function Layout() {
  const [collapsed, setCollapsed] = useCollapsedSidebar();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  // Set when a link such as /?help_ticket=42 (the HR approval email) asks to open one request.
  const [helpView, setHelpView] = useState<HelpCenterView | undefined>(undefined);
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSession();
  useDocumentTitle();

  // Unread replies on the person's requests, shown as a badge on the Help button.
  const { count: helpUnread, refresh: refreshUnread } = useServiceDeskUnread(cockpitServiceDeskClient, {
    enabled: !!user,
  });

  // Store-only accounts (store staff signing in with Google) get a dedicated
  // mobile shell: no department sidebar, just the topbar and their store
  // tools. The store home at /stores is their landing page.
  const storeOnly = isStoreOnlyUser(user?.permissions);

  // Auto-close the mobile drawer when the route changes.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // /?help_ticket=42 opens the Help Center on request 42, then drops the parameter so a refresh does not reopen it.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const id = Number(params.get('help_ticket'));
    if (!Number.isInteger(id) || id <= 0) return;
    setHelpView({ ticket: id });
    setHelpOpen(true);
    params.delete('help_ticket');
    const search = params.toString();
    navigate({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true });
  }, [location.pathname, location.search, navigate]);

  // A page can ask for the Help Center (optionally on one article) through `openHelp` in `lib/helpLauncher.ts`.
  useEffect(() => {
    const onOpen = (e: Event) => {
      setHelpView((e as CustomEvent<OpenHelpDetail>).detail?.view);
      setHelpOpen(true);
    };
    window.addEventListener(OPEN_HELP_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_HELP_EVENT, onOpen);
  }, []);

  const closeHelp = () => {
    setHelpOpen(false);
    setHelpView(undefined);
  };

  // `?help=1` on any page opens its help article on arrival, so a What's new
  // entry or an email can link straight to a page with its article showing.
  // The flag is removed from the address once used, so a refresh does not
  // reopen the panel.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('help') !== '1') return;
    setHelpOpen(true);
    params.delete('help');
    const rest = params.toString();
    navigate(`${location.pathname}${rest ? `?${rest}` : ''}${location.hash}`, { replace: true });
  }, [location.pathname, location.search, location.hash, navigate]);

  const onToggle = () => {
    if (isMobile) setMobileOpen((v) => !v);
    else setCollapsed(!collapsed);
  };

  if (storeOnly) {
    return (
      <AuthGate>
        <div className="flex h-[100dvh] flex-col overflow-hidden bg-amp-cream text-amp-ink dark:bg-amp-dark-bg dark:text-amp-dark-ink">
          <Header
            collapsed={false}
            onToggleCollapsed={() => {}}
            hideSidebarToggle
            onHelpOpen={() => setHelpOpen(true)}
            helpUnread={helpUnread}
          />
          <main data-cockpit-main className="min-w-0 flex-1 overflow-y-auto px-4 py-6 sm:px-8 sm:py-8">
            <Outlet />
          </main>
          <CockpitHelpCenter
            open={helpOpen}
            onClose={closeHelp}
            initialView={helpView}
            onActivity={() => void refreshUnread()}
          />
          <ChatBubbleMount />
        </div>
      </AuthGate>
    );
  }

  return (
    <AuthGate>
      <div className="flex h-[100dvh] flex-col overflow-hidden bg-amp-cream text-amp-ink dark:bg-amp-dark-bg dark:text-amp-dark-ink">
        <Header
          collapsed={collapsed}
          onToggleCollapsed={onToggle}
          onHelpOpen={() => setHelpOpen(true)}
          helpUnread={helpUnread}
        />
        <div className="relative flex min-h-0 flex-1">
          <Sidebar
            collapsed={collapsed}
            isMobile={isMobile}
            mobileOpen={mobileOpen}
            onExpand={() => setCollapsed(false)}
          />
          {isMobile && mobileOpen ? (
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setMobileOpen(false)}
              className="fixed inset-x-0 bottom-0 z-30 bg-black/30"
              style={{ top: 56 }}
            />
          ) : null}
          <main data-cockpit-main className="min-w-0 flex-1 overflow-y-auto px-4 py-6 sm:px-8 sm:py-8 lg:px-10">
            <Outlet />
          </main>
        </div>
        <CockpitHelpCenter
            open={helpOpen}
            onClose={closeHelp}
            initialView={helpView}
            onActivity={() => void refreshUnread()}
          />
        <ChatBubbleMount />
      </div>
    </AuthGate>
  );
}
